import { Navigate, Route, Routes } from "react-router-dom";

import NotFoundPage from "components/error/NotFoundPage";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { isSaasDistribution, isSelfHostedDistribution } from "utils/distribution";
import { isFedRampRegion } from "utils/region";
import useIsOnlySpaceAdmin from "shared/Space/useIsOnlySpaceAdmin";
import useAacFlags from "hooks/useAacFlags";

import { SubscriptionContext } from "../SubscriptionWrapper";
import AccountDeletion from "./AccountDeletion";
import AISettings from "./AI";
import ApiKeysV2 from "./ApiKeysV2";
import Billing from "./billing/Billing";
import BillingNew from "./BillingNew";
import { isV4BillingTier } from "./BillingNew/utils";
import IdpGroupMappingV2 from "./IdpGroupMappingV2";
import Limits from "./Limits";
import LoginPolicies from "./LoginPolicies";
import ManagementStrategy from "./ManagementStrategy";
import ManageMFA from "./ManageMFA";
import RoleCreate from "./Role/Create";
import Roles from "./Roles";
import SecuritySettings from "./SecuritySettings";
import SelfHostedUsageExport from "./SelfHostedUsageExport";
import Sessions from "./Sessions";
import SettingsSingleSignOn from "./SingleSignOn";
import OidcSubjectTemplate from "./OidcSubjectTemplate";
import RuntimeSecurity from "./RuntimeSecurity";
import Spaces from "./Spaces";
import Usage from "./Usage";
import Users from "./Users";

const isSaas = isSaasDistribution();
const isSelfHosted = isSelfHostedDistribution();
const isFedRamp = isFedRampRegion();

type SettingsRoutesProps = {
  securityEmail?: string;
};

const SettingsRoutes = (props: SettingsRoutesProps) => {
  const { billingUsage, runSummarizationAi, oidcSubjectTemplateFrontend } = useTypedFlags();
  const { securityEmail } = props;

  const { tier } = useTypedContext(SubscriptionContext);

  const { nonRootAdminRoleBindings } = useAacFlags();
  const isOnlySpaceAdmin = useIsOnlySpaceAdmin();

  if (isOnlySpaceAdmin) {
    return (
      <Routes>
        <Route index element={<Navigate to="roles" replace />} />
        <Route path="roles" element={<Roles readOnly />} />
        {nonRootAdminRoleBindings && (
          <>
            <Route path="users" element={<Users />} />
            <Route path="idp-group-mapping" element={<IdpGroupMappingV2 />} />
            <Route path="api-keys" element={<ApiKeysV2 />} />
          </>
        )}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route index element={<Navigate to="management-strategy" replace />} />

      <Route path="sso">
        <Route index path="*" element={<SettingsSingleSignOn />} />
      </Route>

      <Route path="mfa" element={<ManageMFA />} />
      <Route path="slack" element={<Navigate to="/settings/integrations" replace />} />
      <Route path="api-keys" element={<ApiKeysV2 />} />
      <Route path="spaces" element={<Spaces />} />
      <Route path="login-policy" element={<LoginPolicies />} />
      <Route path="audit-trail" element={<Navigate to="/audit-trail" replace />} />
      {isSaas && <Route path="limits" element={<Limits />} />}
      {isSaas && !isFedRamp && (
        <Route path="billing">
          <Route index path="*" element={isV4BillingTier(tier) ? <BillingNew /> : <Billing />} />
        </Route>
      )}
      {isSaas && billingUsage && (
        <Route path="usage">
          <Route index path="*" element={<Usage />} />
        </Route>
      )}

      {isSaas && typeof securityEmail !== "undefined" && (
        <Route path="security" element={<SecuritySettings securityEmail={securityEmail} />} />
      )}
      <Route path="runtime-security" element={<RuntimeSecurity />} />
      {oidcSubjectTemplateFrontend && (
        <Route path="oidc-subject-template" element={<OidcSubjectTemplate />} />
      )}
      <Route path="sessions" element={<Sessions />} />
      <Route path="users" element={<Users />} />
      <Route path="idp-group-mapping" element={<IdpGroupMappingV2 />} />
      <Route path="management-strategy" element={<ManagementStrategy />} />

      <Route path="roles" element={<Roles />} />
      <Route path="new/role" element={<RoleCreate />} />
      <Route path="integrations" element={<Navigate to="/integrations" replace />} />
      {runSummarizationAi && <Route path="ai" element={<AISettings />} />}
      {isSelfHosted && <Route path="usage-export" element={<SelfHostedUsageExport />} />}
      {isSaas && <Route path="account-deletion" element={<AccountDeletion />} />}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default SettingsRoutes;
